import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { format } from "date-fns";
import { ArrowLeft, Check } from "lucide-react";
import { bookingConfig } from "../../../../../data/booking/bookingConfig";
import "./ContactForm.css";

gsap.registerPlugin(ScrollTrigger);

const ContactForm = ({ bookingData, onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    clinicName: "",
    website: "",
    services: [],
    message: "",
  });

  const [errors, setErrors] = useState({});

  const formRef = useRef(null);
  const headerRef = useRef(null);
  const backBtnRef = useRef(null);

  const serviceOptions = [
    "Lead Generation",
    "Social Media & Calendar Management",
    "Automations",
    "Website Design & Optimization",
  ];

  // Initial fade-in animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate back button
      gsap.from(backBtnRef.current, {
        opacity: 0,
        x: -20,
        duration: 0.4,
        ease: "power3.out",
      });

      // Animate header
      gsap.from(headerRef.current, {
        opacity: 0,
        y: -20,
        duration: 0.5,
        ease: "power3.out",
        delay: 0.1,
      });

      // Animate form fields with ScrollTrigger
      const formRows = formRef.current.querySelectorAll(".contact-form__row");
      const formFields = formRef.current.querySelectorAll(
        ".contact-form__field--full",
      );
      const submitBtn = formRef.current.querySelector(".contact-form__submit");
      const disclaimer = formRef.current.querySelector(
        ".contact-form__disclaimer",
      );

      // Animate rows
      formRows.forEach((row, index) => {
        gsap.from(row, {
          opacity: 0,
          y: 30,
          duration: 0.6,
          ease: "power3.out",
          delay: 0.2 + index * 0.1,
          scrollTrigger: {
            trigger: row,
            start: "top 85%",
            toggleActions: "play none none none",
            once: true,
          },
        });
      });

      // Animate full-width fields
      formFields.forEach((field, index) => {
        gsap.from(field, {
          opacity: 0,
          y: 30,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: field,
            start: "top 85%",
            toggleActions: "play none none none",
            once: true,
          },
        });
      });

      // Animate submit button
      gsap.from(submitBtn, {
        opacity: 0,
        y: 30,
        scale: 0.9,
        duration: 0.6,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: submitBtn,
          start: "top 90%",
          toggleActions: "play none none none",
          once: true,
        },
      });

      // Animate disclaimer
      gsap.from(disclaimer, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: disclaimer,
          start: "top 90%",
          toggleActions: "play none none none",
          once: true,
        },
      });
    }, formRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleServiceToggle = (service) => {
    const currentServices = formData.services;
    if (currentServices.includes(service)) {
      setFormData({
        ...formData,
        services: currentServices.filter((s) => s !== service),
      });
    } else {
      setFormData({
        ...formData,
        services: [...currentServices, service],
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = "Clinic name is required";
    }

    if (formData.services.length === 0) {
      newErrors.services = "Please select at least one service";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const response = await fetch("/api/book", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            clinicName: formData.clinicName,
            website: formData.website,
            service: formData.services.join(", "),
            date: format(bookingData.date, "yyyy-MM-dd"),
            time: format(bookingData.time, "HH:mm"),
            duration: bookingData.duration,
            message: formData.message,
          }),
        });

        const result = await response.json();

        if (result.success) {
          // Fade out before transition
          gsap.to(formRef.current, {
            opacity: 0,
            y: -20,
            duration: 0.3,
            ease: "power2.in",
            onComplete: () => onSubmit(formData),
          });
        } else {
          alert("Booking failed: " + result.error);
        }
      } catch (err) {
        console.error("Booking error:", err);
        alert("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className="contact-form" ref={formRef}>
      <button className="contact-form__back" onClick={onBack} ref={backBtnRef}>
        <ArrowLeft size={20} />
        Back to Time Selection
      </button>

      <div className="contact-form__header" ref={headerRef}>
        <h2 className="contact-form__title">Your Details</h2>
        <p className="contact-form__subtitle">
          {format(bookingData.date, "EEEE, MMMM do")} at{" "}
          {format(bookingData.time, "h:mm a")} ({bookingData.duration} minutes)
        </p>
      </div>

      <form className="contact-form__form" onSubmit={handleSubmit}>
        {/* Name Fields */}
        <div className="contact-form__row">
          <div className="contact-form__field">
            <label className="contact-form__label">
              First Name <span className="contact-form__required">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`contact-form__input ${
                errors.firstName ? "contact-form__input--error" : ""
              }`}
              placeholder="John"
            />
            {errors.firstName && (
              <span className="contact-form__error">{errors.firstName}</span>
            )}
          </div>

          <div className="contact-form__field">
            <label className="contact-form__label">
              Last Name <span className="contact-form__required">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`contact-form__input ${
                errors.lastName ? "contact-form__input--error" : ""
              }`}
              placeholder="Doe"
            />
            {errors.lastName && (
              <span className="contact-form__error">{errors.lastName}</span>
            )}
          </div>
        </div>

        {/* Email & Phone */}
        <div className="contact-form__row">
          <div className="contact-form__field">
            <label className="contact-form__label">
              Email <span className="contact-form__required">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`contact-form__input ${
                errors.email ? "contact-form__input--error" : ""
              }`}
              placeholder="john@clinic.com"
            />
            {errors.email && (
              <span className="contact-form__error">{errors.email}</span>
            )}
          </div>

          <div className="contact-form__field">
            <label className="contact-form__label">Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="contact-form__input"
              placeholder="+44 123 456 7890"
            />
          </div>
        </div>

        {/* Clinic Name & Website */}
        <div className="contact-form__row">
          <div className="contact-form__field">
            <label className="contact-form__label">
              Clinic Name <span className="contact-form__required">*</span>
            </label>
            <input
              type="text"
              name="clinicName"
              value={formData.clinicName}
              onChange={handleChange}
              className={`contact-form__input ${
                errors.clinicName ? "contact-form__input--error" : ""
              }`}
              placeholder="Your Aesthetic Clinic"
            />
            {errors.clinicName && (
              <span className="contact-form__error">{errors.clinicName}</span>
            )}
          </div>

          <div className="contact-form__field">
            <label className="contact-form__label">Website (Optional)</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="contact-form__input"
              placeholder="https://yourclinic.com"
            />
          </div>
        </div>

        {/* Services Selection */}
        <div className="contact-form__field contact-form__field--full">
          <label className="contact-form__label">
            Services Interested In{" "}
            <span className="contact-form__required">*</span>
          </label>
          <div className="contact-form__services">
            {serviceOptions.map((service) => (
              <button
                key={service}
                type="button"
                className={`contact-form__service-btn ${
                  formData.services.includes(service)
                    ? "contact-form__service-btn--active"
                    : ""
                }`}
                onClick={() => handleServiceToggle(service)}
              >
                {formData.services.includes(service) && (
                  <Check size={16} className="contact-form__service-check" />
                )}
                {service}
              </button>
            ))}
          </div>
          {errors.services && (
            <span className="contact-form__error">{errors.services}</span>
          )}
        </div>

        {/* Message */}
        <div className="contact-form__field contact-form__field--full">
          <label className="contact-form__label">
            Additional Information (Optional)
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="contact-form__textarea"
            placeholder="Tell us about your clinic, current marketing challenges, or specific goals..."
            rows="5"
          />
        </div>

        {/* Submit Button */}
        <button type="submit" className="contact-form__submit">
          Confirm Booking
        </button>

        <p className="contact-form__disclaimer">
          By submitting this form, you agree to receive email communication from
          ASCENCIO Group. We respect your privacy and will never share your
          information.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
